import { useTranslation } from 'react-i18next';

import {
	Box,
	Chip,
	LinearProgress,
	ListItemText,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';

import moment from 'moment';

import {
	WalletOperation,
	WalletOperationSortField,
	WalletOperationSortOrder,
	walletOperationsRowsPerPageOptions,
	isWalletOperationOpen,
} from 'lib/models/walletOperations';
import { price } from 'lib/numbers';

import useIsMobile from 'hooks/useIsMobile';
import useWalletOperationLabels from 'hooks/useWalletOperationLabels';

import ResponsiveTable from 'components/ResponsiveTable';

type Props = {
	loading?: boolean;
	operations: WalletOperation[];
	onOpen: (operation: WalletOperation) => void;
	/** How many operations match the filters, as the API counted them. */
	total: number;
	/** 0-based, as MUI's pagination counts. */
	page: number;
	rowsPerPage: number;
	/** Which column the API ordered the answer by, and in which direction. */
	sort: WalletOperationSortField;
	order: WalletOperationSortOrder;
	onSortChange: (field: WalletOperationSortField) => void;
	onPageChange: (page: number) => void;
	onRowsPerPageChange: (rowsPerPage: number) => void;
};

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

/**
 * A date as the matching procedure needs to read it, or a dash when it was never written.
 *
 * `firstSentAt` is absent exactly when the request never reached the provider, which is a
 * fact about the operation and not a gap in the screen.
 */
function when(value?: string): string {
	return value ? moment(value).format(DATE_FORMAT) : '-';
}

/**
 * The operations of the queue on screen, one page of them.
 *
 * The rows are whatever the API answered with: the filtering, the order and the window are
 * the endpoint's, so nothing is narrowed, sorted or sliced here — the two sortable columns
 * ask it for another order rather than rearranging the page it sent. Opening a row reads the
 * operation again and shows its detail, which is where the three actions live.
 */
export default function WalletOperationsTable({
	loading,
	operations,
	onOpen,
	total,
	page,
	rowsPerPage,
	sort,
	order,
	onSortChange,
	onPageChange,
	onRowsPerPageChange,
}: Props) {
	const { t } = useTranslation();
	const { kindLabel, stateLabel, outcomeLabel, stageLabel } = useWalletOperationLabels();
	const isMobile = useIsMobile();

	/**
	 * A column header that reorders the listing.
	 *
	 * Only the two the API indexes are offered: it refuses any other sort key, and a header
	 * that looked sortable and was not is worse than one that never offered it.
	 */
	function sortableCell(field: WalletOperationSortField, label: string) {
		return (
			<TableCell align="left" padding="normal" sortDirection={sort === field ? order : false}>
				<TableSortLabel
					active={sort === field}
					direction={sort === field ? order : 'desc'}
					onClick={() => onSortChange(field)}
				>
					{label}
				</TableSortLabel>
			</TableCell>
		);
	}

	/**
	 * What the row says about itself before anybody opens it: whether the reconciler gave up
	 * on it, whether another operator is holding it, and whether the provider can still be
	 * asked about it. The last two are the API's own facts, not a guess from the record.
	 */
	function marks(operation: WalletOperation) {
		// A closed operation is not waiting for anything, so none of the three applies to it.
		if (!isWalletOperationOpen(operation)) return null;

		return (
			<>
				<Chip
					size="small"
					label={stageLabel(operation.stage)}
					color={operation.stage === 'unresolved' ? 'error' : 'default'}
				/>
				{operation.locked && <Chip size="small" color="warning" label={t('common:walletOperationLocked')} />}
				{!operation.pollable && <Chip size="small" label={t('common:walletOperationNotPollable')} />}
			</>
		);
	}

	return (
		<Box>
			{!!loading && <LinearProgress />}

			{!loading && !operations.length && (
				<Typography sx={{ padding: 2 }}>{t('common:walletOperationsEmpty')}</Typography>
			)}

			{/* The list has no footer of its own, so the window is driven from above it, as
			    the other paged listings of this backoffice do on mobile. */}
			{isMobile && (
				<TablePagination
					component="div"
					labelRowsPerPage={t('common:rowsPerPage')}
					count={total}
					page={page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={walletOperationsRowsPerPageOptions}
					onPageChange={(_, next) => onPageChange(next)}
					onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
					variant="head"
				/>
			)}

			<ResponsiveTable
				elements={operations}
				loading={loading}
				listSx={{ maxHeight: '70vh' }}
				tableSx={{ maxHeight: '65vh' }}
				list={{
					primaryKey: 'id',
					renderListItemText: (operation) => (
						<ListItemText
							onClick={() => onOpen(operation)}
							primary={
								<Box>
									<Typography>
										{kindLabel(operation.kind)} — {price(operation.amount)}
									</Typography>
									<Typography>{operation.subject}</Typography>
									<Typography>{when(operation.createdAt)}</Typography>
									<Typography>{stateLabel(operation.state)}</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, paddingY: 0.5 }}>{marks(operation)}</Box>
									<Typography variant="body2">
										{operation.reconcile?.reason || t('common:walletOperationReasonNone')}
									</Typography>
								</Box>
							}
						/>
					),
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationKind')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationSubject')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationActor')}
								</TableCell>
								{sortableCell('amount', t('common:walletOperationAmount'))}
								{sortableCell('createdAt', t('common:walletOperationCreatedAt'))}
								<TableCell align="left" padding="normal">
									{t('common:walletOperationFirstSentAt')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationState')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationOutcome')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationProvider')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:walletOperationReason')}
								</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (operation) => (
						<TableRow
							hover
							tabIndex={-1}
							key={operation.id}
							sx={{ cursor: 'pointer' }}
							onClick={() => onOpen(operation)}
						>
							<TableCell>
								<Box>
									<Typography variant="body2">{kindLabel(operation.kind)}</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{marks(operation)}</Box>
								</Box>
							</TableCell>
							<TableCell>{operation.subject}</TableCell>
							<TableCell>{operation.actor}</TableCell>
							<TableCell>{price(operation.amount)}</TableCell>
							<TableCell>{when(operation.createdAt)}</TableCell>
							<TableCell>{when(operation.provider?.firstSentAt)}</TableCell>
							<TableCell>{stateLabel(operation.state)}</TableCell>
							<TableCell>{outcomeLabel(operation.outcome)}</TableCell>
							<TableCell>
								<Typography variant="body2">{operation.provider?.endpoint || '-'}</Typography>
								<Typography variant="body2">
									{operation.provider?.loadType || '-'} · {operation.provider?.attempts ?? 0}
								</Typography>
							</TableCell>
							<TableCell sx={{ maxWidth: 280 }}>
								{operation.reconcile?.reason || t('common:walletOperationReasonNone')}
							</TableCell>
						</TableRow>
					),
					pagination: {
						count: total,
						page,
						rowsPerPage,
						rowsPerPageOptions: walletOperationsRowsPerPageOptions,
						onPageChange: (_, next) => onPageChange(next),
						onRowsPerPageChange: (event) => onRowsPerPageChange(Number(event.target.value)),
					},
				}}
			/>
		</Box>
	);
}
